// paddock/src/components/ui/alert-dialog.tsx
import { cn } from '@/lib/utils';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';

const baseButton =
  'inline-flex items-center justify-center rounded px-[15px] text-[15px] leading-none h-[35px] select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1';

const buttonVariants: Record<ButtonVariant, string> = {
  red: 'bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-md',
  mauve: 'bg-gray-100 text-gray-800 hover:bg-gray-200 px-4 py-2 rounded-md',
};

const AlertDialog = AlertDialogPrimitive.Root;
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 bg-gray-200/20', className)}
    {...props}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  return (
    <AlertDialogPrimitive.Content
      ref={ref}
      {...props}
      className={cn(
        'fixed left-1/2 top-1/3 z-50 w-[90vw] max-w-[500px] max-h-[85vh] -translate-x-1/2 -translate-y-1/2',
        'rounded-md bg-white p-6 shadow-lg outline-none',
        className,
      )}
    >
      {children}
    </AlertDialogPrimitive.Content>
  );
});
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogTitle = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    {...props}
    className={cn('m-0 text-[17px] text-[var(--mauve-12)]', className)}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    {...props}
    className={cn(
      'mb-5 text-[15px] leading-[1.5] text-[var(--mauve-11)]',
      className,
    )}
  />
));
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

type ButtonVariant = 'red' | 'mauve';

const AlertDialogAction = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> & {
    variant?: ButtonVariant;
  }
>(({ className, variant = 'red', ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    {...props}
    className={`${baseButton} ${buttonVariants[variant]} ${className ?? ''}`}
  />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> & {
    variant?: ButtonVariant;
  }
>(({ className, variant = 'mauve', ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    {...props}
    className={`${baseButton} ${buttonVariants[variant]} ${className ?? ''}`}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
};
